import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useMediaQuery } from 'react-responsive';
import { format } from 'date-fns';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem,
  Paper, Select, Tab, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import StoreIcon from '@mui/icons-material/Store';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';

// Field groups. Every column in DIM_STORE goes somewhere. Order within a
// group is form-visual order; server accepts columns in any order.
const FIELD_GROUPS = [
  {
    title: 'Identity',
    fields: [
      { name: 'STORE_ID', label: 'Store ID', type: 'number', required: true, autoFocus: true },
      { name: 'STORE_NAME', label: 'Store Name', type: 'text', placeholder: 'e.g. 25 - Foothill Ranch' },
      { name: 'STORE_NAME_SHORT', label: 'Store Name (short)', type: 'text', placeholder: 'e.g. 25-Foothill Ranch' },
      { name: 'OLO_VENDOR_NAME', label: 'OLO Vendor Name', type: 'text' },
      { name: 'STORE_NAME_YEXT', label: 'Store Name (Yext)', type: 'text' },
      { name: 'STORE_TYPE', label: 'Store Type', type: 'text', placeholder: 'e.g. Traditional / Lab / Kitchen' },
      { name: 'STORE_TYPE_OPEN', label: 'Store Type (Open)', type: 'text', placeholder: 'e.g. 7 Day' },
      { name: 'EMAIL', label: 'Email', type: 'text', autoFillFromStoreId: (id) => `mendo${id}@mendocinofarms.com` },
    ],
  },
  {
    title: 'Cross-system IDs',
    fields: [
      { name: 'STORE_NUMBER_OLO', label: 'OLO #', type: 'number' },
      { name: 'STORE_NUMBER_DAYFORCE', label: 'Dayforce #', type: 'number' },
      { name: 'STORE_NUMBER_DAYFORCE_XREF', label: 'Dayforce XREF', type: 'text' },
      { name: 'STORE_NUMBER_ALOHA', label: 'Aloha #', type: 'number' },
      { name: 'STORE_NUMBER_HOTSCHEDULES', label: 'HotSchedules #', type: 'number' },
      { name: 'STORE_NUMBER_CTUIT', label: 'CTUIT #', type: 'text' },
      { name: 'STORE_NUMBER_CRUNCHTIME', label: 'CrunchTime #', type: 'text' },
      { name: 'STORE_NUMBER_YEXT', label: 'Yext #', type: 'text' },
      { name: 'TOAST_GUID', label: 'Toast GUID', type: 'text' },
      { name: 'DAYFORCE_TIMECLOCK_ID', label: 'Dayforce Timeclock ID', type: 'text' },
      { name: 'KONDUCTER_STORE_ID', label: 'Konducter Store ID', type: 'text' },
      { name: 'ZENDESK_LOCATION_TAG', label: 'Zendesk Location Tag', type: 'text' },
    ],
  },
  {
    title: 'Address',
    fields: [
      { name: 'ADDRESS_LINE_1', label: 'Address Line 1', type: 'text' },
      { name: 'ADDRESS_LINE_2', label: 'Address Line 2', type: 'text' },
      { name: 'CITY', label: 'City', type: 'text' },
      { name: 'COUNTY', label: 'County', type: 'text' },
      { name: 'STATE_CODE', label: 'State Code', type: 'text', placeholder: 'CA' },
      { name: 'ZIPCODE', label: 'Zip Code', type: 'number' },
      { name: 'PHONE_NUMBER', label: 'Phone Number', type: 'text' },
      { name: 'LATTITUDE', label: 'Latitude', type: 'text' },
      { name: 'LONGITUDE', label: 'Longitude', type: 'text' },
      { name: 'TIME_ZONE_UTC', label: 'Time Zone (UTC offset)', type: 'number' },
    ],
  },
  {
    title: 'Operations',
    fields: [
      { name: 'AREA', label: 'Area', type: 'area-select' },
      { name: 'AREA_ID', label: 'Area ID', type: 'text', readOnly: true, helper: 'Auto from Area' },
      { name: 'AREA_SORT_ORDER', label: 'Area Sort Order', type: 'number', readOnly: true, helper: 'Auto from Area' },
      { name: 'REGION', label: 'Region', type: 'text', readOnly: true, helper: 'Auto from Area' },
      { name: 'REGION_ID', label: 'Region ID', type: 'number', readOnly: true, helper: 'Auto from Area' },
      { name: 'REGION_SORT_ORDER', label: 'Region Sort Order', type: 'number', readOnly: true, helper: 'Auto from Area' },
      { name: 'DIRECTOR_ID', label: 'Director ID', type: 'text', readOnly: true, helper: 'Auto from Area' },
      { name: 'DIRECTOR_OPERATIONS', label: 'Director of Operations', type: 'text', readOnly: true, helper: 'Auto from Area' },
      { name: 'DIRECTOR_EMAIL_ADDRESS', label: 'Director Email', type: 'text', readOnly: true, helper: 'Auto from Area' },
      { name: 'USF_DC', label: 'USF DC', type: 'text', placeholder: 'e.g. USF SoCal' },
    ],
  },
  {
    title: 'Dates',
    fields: [
      { name: 'OPEN_DATE', label: 'Open Date', type: 'date' },
      { name: 'COMP_DATE', label: 'Comp Date', type: 'date' },
      { name: 'LABOR_DATE', label: 'Labor Date', type: 'date' },
      { name: 'TOAST_LIVE_DATE', label: 'Toast Live Date', type: 'text', placeholder: 'YYYYMMDD (as stored)' },
      { name: 'CLOSE_DATE', label: 'Close Date', type: 'date' },
      { name: 'VINTAGE', label: 'Vintage (year)', type: 'number' },
    ],
  },
  {
    title: 'Flags & Misc',
    fields: [
      { name: 'OPEN_FLAG', label: 'Open Flag (0/1)', type: 'number' },
      { name: 'NSO_FLAG', label: 'NSO Flag (0/1)', type: 'number' },
      { name: 'WEEKDAY_ONLY_FLAG', label: 'Weekday Only Flag (0/1)', type: 'number' },
      { name: 'SQUARE_FEET', label: 'Square Feet', type: 'text' },
    ],
  },
];

export default function DimStoreAdmin() {
  const isTabletOrMobile = useMediaQuery({ maxWidth: 900 });
  const { user: firebaseUser } = useUserAuth();
  const [tab, setTab] = useState(0); // 0 = New, 1 = Existing
  const [values, setValues] = useState({});
  const [areas, setAreas] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dupWarn, setDupWarn] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  // Existing tab state
  const [listRows, setListRows] = useState(null); // null = not loaded yet
  const [listLoading, setListLoading] = useState(false);
  const [detailStoreId, setDetailStoreId] = useState(null);
  const [detailValues, setDetailValues] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const getToken = useCallback(async () => {
    if (!firebaseUser?.getIdToken) return null;
    return firebaseUser.getIdToken(true);
  }, [firebaseUser]);

  // Fetch areas once. If we get a 403 the user isn't allowed here — flip
  // the whole page to a forbidden state so the form never renders.
  useEffect(() => {
    (async () => {
      const token = await getToken();
      if (!token) return;
      try {
        setLoading(true);
        const { data } = await axios.get('/hd-api/dim-store-admin/areas', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAreas(data?.areas || []);
      } catch (e) {
        if (e.response?.status === 403) setForbidden(true);
        else setError(e.response?.data?.error || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken]);

  // Watch STORE_ID → auto-populate EMAIL when user hasn't manually typed one.
  useEffect(() => {
    const id = parseInt(values.STORE_ID, 10);
    if (!Number.isFinite(id)) return;
    setValues((v) => {
      const next = { ...v };
      FIELD_GROUPS.forEach((g) => g.fields.forEach((f) => {
        if (f.autoFillFromStoreId && !v[`__manual_${f.name}`]) {
          next[f.name] = f.autoFillFromStoreId(id);
        }
      }));
      return next;
    });
  }, [values.STORE_ID]);

  // Debounced duplicate check when STORE_ID is stable.
  useEffect(() => {
    const id = parseInt(values.STORE_ID, 10);
    if (!Number.isFinite(id)) { setDupWarn(null); return; }
    const t = setTimeout(async () => {
      const token = await getToken();
      if (!token) return;
      try {
        const { data } = await axios.get(
          `/hd-api/dim-store-admin/check-id?storeId=${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDupWarn(data.exists ? `STORE_ID ${id} already exists as "${data.existing?.STORE_NAME}"` : null);
      } catch { /* silent — not blocking */ }
    }, 500);
    return () => clearTimeout(t);
  }, [values.STORE_ID, getToken]);

  // Lazy-load the list only when the Existing tab is first activated so we
  // don't pay the query on every page visit.
  useEffect(() => {
    if (tab !== 1 || listRows !== null || listLoading) return;
    (async () => {
      setListLoading(true);
      try {
        const token = await getToken();
        const { data } = await axios.get('/hd-api/dim-store-admin/list', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setListRows(data?.rows || []);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      } finally {
        setListLoading(false);
      }
    })();
  }, [tab, listRows, listLoading, getToken]);

  // Fetch full detail when a row is clicked.
  useEffect(() => {
    if (detailStoreId == null) { setDetailValues(null); return; }
    (async () => {
      setDetailLoading(true);
      setDetailValues(null);
      try {
        const token = await getToken();
        const { data } = await axios.get(
          `/hd-api/dim-store-admin/store/${detailStoreId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setDetailValues(data?.store || null);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      } finally {
        setDetailLoading(false);
      }
    })();
  }, [detailStoreId, getToken]);

  const setValue = (name, value) => {
    setValues((v) => ({ ...v, [name]: value, [`__manual_${name}`]: true }));
  };

  const onAreaChange = (areaName) => {
    const match = areas.find((a) => a.AREA === areaName);
    setValues((v) => ({
      ...v,
      AREA: areaName,
      AREA_ID: match?.AREA_ID ?? '',
      AREA_SORT_ORDER: match?.AREA_SORT_ORDER ?? '',
      REGION: match?.REGION ?? '',
      REGION_ID: match?.REGION_ID ?? '',
      REGION_SORT_ORDER: match?.REGION_SORT_ORDER ?? '',
      DIRECTOR_ID: match?.DIRECTOR_ID ?? '',
      DIRECTOR_OPERATIONS: match?.DIRECTOR_OPERATIONS ?? '',
      DIRECTOR_EMAIL_ADDRESS: match?.DIRECTOR_EMAIL_ADDRESS ?? '',
    }));
  };

  // Build the payload — strip empty strings, format dates, drop __manual_* keys.
  const payload = useMemo(() => {
    const out = {};
    FIELD_GROUPS.forEach((g) => g.fields.forEach((f) => {
      const raw = values[f.name];
      if (raw === undefined || raw === null || raw === '') return;
      if (f.type === 'date') out[f.name] = format(raw, 'yyyy-MM-dd');
      else if (f.type === 'number') {
        const n = Number(raw);
        if (Number.isFinite(n)) out[f.name] = n;
      } else out[f.name] = raw;
    }));
    return out;
  }, [values]);

  const canSubmit = Number.isFinite(parseInt(values.STORE_ID, 10)) && !dupWarn && !submitting;

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const token = await getToken();
      const { data } = await axios.post('/hd-api/dim-store-admin/insert', payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(data);
      setConfirmOpen(false);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (forbidden) {
    return (
      <div>
        <Header isTabletOrMobile={isTabletOrMobile} />
        <Container maxWidth="md">
          <Logo isTabletOrMobile={isTabletOrMobile} />
          <Alert severity="error" sx={{ mt: 4 }}>
            You don't have access to this page.
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Box sx={{
              backgroundColor: 'white',
              borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
              px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2,
            }}>
              <Box sx={{
                backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                borderRadius: 2, p: 1, display: 'flex', alignItems: 'center',
              }}>
                <StoreIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato' }}>
                  DIM_STORE Admin
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  THE_WAREHOUSE.DIMENSION.DIM_STORE
                </Typography>
              </Box>
            </Box>
            <Tabs
              value={tab}
              onChange={(_, v) => { setTab(v); setDetailStoreId(null); }}
              sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            >
              <Tab label="New Record" />
              <Tab label={`Existing (${listRows?.length ?? '…'})`} />
            </Tabs>
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {tab === 0 && (
            <>
              {dupWarn && <Alert severity="warning" sx={{ mb: 2 }}>{dupWarn}</Alert>}
              {result && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResult(null)}>
                  Inserted STORE_ID {result.inserted?.STORE_ID} ({result.columnsWritten?.length} columns written).
                </Alert>
              )}
              {loading && <CircularProgress size={20} />}

              {FIELD_GROUPS.map((group) => (
                <Paper key={group.title} variant="outlined" sx={{ mb: 2 }}>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2">{group.title}</Typography>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      {group.fields.map((f) => (
                        <Grid item xs={12} sm={6} md={4} key={f.name}>
                          <FieldInput
                            field={f}
                            value={values[f.name] ?? ''}
                            onChange={(v) => f.name === 'AREA' ? onAreaChange(v) : setValue(f.name, v)}
                            areas={areas}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Paper>
              ))}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  disabled={!canSubmit}
                  startIcon={<SaveIcon />}
                  onClick={() => setConfirmOpen(true)}
                >
                  Review & Insert
                </Button>
              </Box>
            </>
          )}

          {tab === 1 && (
            <>
              {listLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
              )}

              {!listLoading && detailStoreId == null && (
                <ExistingStoreTable
                  rows={listRows || []}
                  onOpen={(id) => setDetailStoreId(id)}
                />
              )}

              {detailStoreId != null && (
                <>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => setDetailStoreId(null)}
                    sx={{ mb: 2 }}
                  >
                    Back to list
                  </Button>
                  {detailLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                  )}
                  {detailValues && (
                    <>
                      {FIELD_GROUPS.map((group) => (
                        <Paper key={group.title} variant="outlined" sx={{ mb: 2 }}>
                          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                            <Typography variant="subtitle2">{group.title}</Typography>
                          </Box>
                          <Box sx={{ p: 2 }}>
                            <Grid container spacing={2}>
                              {group.fields.map((f) => (
                                <Grid item xs={12} sm={6} md={4} key={f.name}>
                                  <FieldInput
                                    field={f}
                                    value={formatDetailValue(detailValues[f.name], f)}
                                    onChange={() => {}}
                                    areas={areas}
                                    forceReadOnly
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        </Paper>
                      ))}
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Container>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Confirm insert into DIM_STORE</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            About to write {Object.keys(payload).length} columns. Any column not listed here will be NULL.
          </Typography>
          <Box component="pre" sx={{ fontSize: 12, bgcolor: 'grey.50', p: 2, borderRadius: 1, overflow: 'auto', maxHeight: 400 }}>
            {JSON.stringify(payload, null, 2)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={14} /> : <SaveIcon />}
          >
            Confirm Insert
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function FieldInput({ field, value, onChange, areas, forceReadOnly }) {
  const readOnly = forceReadOnly || field.readOnly;
  if (field.type === 'area-select') {
    return (
      <FormControl fullWidth size="small" disabled={readOnly}>
        <InputLabel>{field.label}</InputLabel>
        <Select value={value} label={field.label} onChange={(e) => onChange(e.target.value)}>
          <MenuItem value=""><em>(none)</em></MenuItem>
          {areas.map((a) => (
            <MenuItem key={a.AREA} value={a.AREA}>{a.AREA}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }
  return (
    <TextField
      fullWidth
      size="small"
      type={field.type === 'date' ? 'date' : (field.type === 'number' ? 'number' : 'text')}
      label={field.label}
      placeholder={forceReadOnly ? '' : (field.placeholder || '')}
      helperText={forceReadOnly ? '' : (field.helper || '')}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoFocus={!forceReadOnly && field.autoFocus}
      InputProps={{ readOnly }}
      InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
    />
  );
}

// Coerces the value returned by /store/:storeId into what the FieldInput
// expects: date fields get YYYY-MM-DD; nulls become empty strings.
function formatDetailValue(raw, field) {
  if (raw === null || raw === undefined) return '';
  if (field.type === 'date') {
    if (raw instanceof Date) return format(raw, 'yyyy-MM-dd');
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return format(d, 'yyyy-MM-dd');
    return String(raw);
  }
  return String(raw);
}

function ExistingStoreTable({ rows, onOpen }) {
  if (rows.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No stores found.</Typography>
      </Paper>
    );
  }
  return (
    <Paper variant="outlined">
      <TableContainer sx={{ maxHeight: 640 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="right">Store ID</TableCell>
              <TableCell>Store Name</TableCell>
              <TableCell>City</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Area</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="center">Open?</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow
                key={r.STORE_ID}
                hover
                onClick={() => onOpen(r.STORE_ID)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell align="right">{r.STORE_ID}</TableCell>
                <TableCell>{r.STORE_NAME || '—'}</TableCell>
                <TableCell>{r.CITY || '—'}</TableCell>
                <TableCell>{r.STATE_CODE || '—'}</TableCell>
                <TableCell>{r.AREA || '—'}</TableCell>
                <TableCell>{r.REGION || '—'}</TableCell>
                <TableCell>{r.STORE_TYPE || '—'}</TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={r.OPEN_FLAG === 1 ? 'Open' : 'Closed'}
                    color={r.OPEN_FLAG === 1 ? 'success' : 'default'}
                    variant={r.OPEN_FLAG === 1 ? 'filled' : 'outlined'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
